/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ICommerceApiSettings, ICoreContext, IGeneric, IAny } from '@msdyn365-commerce/core-internal';
import { DimensionTypes, generateSwatchImageUrl, StringExtensions, IDimensionValueForSelectedVariant } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import * as React from 'react';
import { IBuyboxCommonResources } from '../../../modules/c-buybox/components/c-buyboxInterface';

/**
 * Swatch item data.
 */
export interface ISwatchItem {
    value: string;
    itemId: string;
    dimensionType: DimensionTypes;
    dimensionId: string;
    inventoryCode: string;
    variantIds?: IDimensionValueForSelectedVariant;
    colorHexCode?: string;
    imageUrl?: string;
    productImageUrls?: string[];
    isDisabled?: boolean;
    isDefault?: boolean;
    swatchItemAriaLabel?: string;
    NB?: boolean;
    itemNumber?: string;
    dimensionTypeId?: number;
}

/**
 * Swatch item configuration.
 */
export interface ISwatchItemProps {
    className: string;
    apiSettings: ICommerceApiSettings;
    requestContext: ICoreContext<IGeneric<IAny>>;
    resources?: IBuyboxCommonResources;
    isSelected: boolean;
    isSelectionEnabled?: boolean;

    item: ISwatchItem;

    onSelectItem(item: ISwatchItem): void;
    context: ICoreContext;
}

/**
 * Represents a single item for swatch.
 * @param props - Item configuration.
 * @returns React node.
 */
export const SwatchItemComponent: React.FC<ISwatchItemProps> = (props: ISwatchItemProps) => {
    const { isSelected, className, onSelectItem, item } = props;
    const { itemId, value, colorHexCode, imageUrl } = item;
    const style: React.CSSProperties = {};

    let isColored = false;

    const hasColor = !StringExtensions.isNullOrWhitespace(colorHexCode);
    const hasImage = !StringExtensions.isNullOrWhitespace(imageUrl);

    let modImageUrl = '';
    if (imageUrl) {
        const splitImgUrl = imageUrl?.split('/');
        // eslint-disable-next-line no-new-wrappers
        const modUrl = new String(`${splitImgUrl[0]}/${item.itemNumber}-${splitImgUrl[1]}`);
        modImageUrl = modUrl.toString();
    } else {
        modImageUrl = '';
    }


    const selectItem = React.useCallback(() => {
        onSelectItem(item);
    }, [onSelectItem, item]);

    const selectionHandler = props.isSelectionEnabled ? selectItem : undefined;
    if (hasColor && item.dimensionType.toLowerCase() === 'color') {
        style.backgroundColor = colorHexCode;
        isColored = true;
    } else if (hasImage) {
        const fullImageUrl = generateSwatchImageUrl(imageUrl!, props.apiSettings);
        const modifiedImageUrl = generateSwatchImageUrl(modImageUrl, props.apiSettings);
        const [imgSrc, setImgSrc] = React.useState(modifiedImageUrl);
        const onImgError = (event: React.SyntheticEvent<HTMLImageElement>) => {
            setImgSrc(fullImageUrl);
            if (!fullImageUrl) {
                event.currentTarget.classList.add('img-error');
            }
        };
        
        if (typeof window !== 'undefined') {
            return (
                <img
                    key={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
                    src={imgSrc ? imgSrc : fullImageUrl}
                    className={
                        classnames(
                            className,
                            `${className}-${itemId}`,
                            `${className}__image`,
                            isSelected ? `${className}__selected` : `${className}__unselected`,
                            item.isDisabled ? `${className}__disabled` : undefined
                        )
                    }
                    style={style}
                    onClick={selectionHandler}
                    onKeyPress={selectionHandler}
                    title={value}
                    aria-label={value}
                    alt={value}
                    data-dimension={item.dimensionType}
                    onError={event => onImgError(event)}
                />
            );
        }
    }

    const shouldDisplayText = !isColored;

    return (
        <div className={classnames(isSelected ? `${className}__selected` : `${className}__unselected`)}>
            <button
                key={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
                className={
                    classnames(
                        className,
                        `${className}-${itemId}`,
                        isSelected ? `${className}__selected` : `${className}__unselected`,
                        isColored ? `${className}__colored` : `${className}__uncolored`,
                        item.isDisabled ? `${className}__disabled` : undefined
                    )
                }
                style={style}
                onClick={selectionHandler}
                onKeyPress={selectionHandler}
                title={value}
                aria-label={value}
                data-dimension={item.dimensionType}
            >
                {shouldDisplayText && value}
            </button>
        </div>
    );
};
